<script setup lang="ts">
import { formControlDisabledSurfaceClasses } from '../form-control/classes';
defineOptions({
    name: 'IdeoFormSegmented'
});

const model = defineModel<any>();
const props = withDefaults(defineProps<{
    options: Record<string, any>[];
    valueField?: string;
    textField?: string;
    iconField?: string;
    disabledField?: string;
    disabled?: boolean;
    full?: boolean;
    contentWeighted?: boolean;
    ariaLabel?: string;
    iconOnly?: boolean;
    name?: string;
}>(), {
    valueField: 'value',
    textField: 'text',
    iconField: 'icon',
    disabledField: 'disabled',
    disabled: false,
    full: false,
    contentWeighted: false,
    ariaLabel: undefined,
    iconOnly: false,
    name: undefined
});

const root = ref<HTMLElement | null>(null);
const indicatorMotionEnabled = ref(false);
const indicator = ref({
    left: 0,
    width: 0,
    ready: false
});
const selectedIndex = computed(() => props.options.findIndex(option => option[props.valueField] === model.value));
const indicatorStyle = computed(() => ({
    left: `${indicator.value.left}px`,
    width: `${indicator.value.width}px`
}));

let resizeObserver: ResizeObserver | null = null;

function optionElements()
{
    return [...(root.value?.querySelectorAll<HTMLButtonElement>('[data-form-segmented-option]') ?? [])];
}

function enabledIndexes()
{
    return props.options
        .map((option, index) => ({ option, index }))
        .filter(({ option }) => !props.disabled && !option[props.disabledField])
        .map(({ index }) => index);
}

function measureIndicator(animate = false)
{
    const selected = optionElements()[selectedIndex.value];

    if (!selected || selected.offsetWidth === 0)
    {
        indicatorMotionEnabled.value = false;
        indicator.value.ready = false;

        return;
    }

    const left = selected.offsetLeft;
    const width = selected.offsetWidth;

    if (indicator.value.ready && indicator.value.left === left && indicator.value.width === width)
        return;

    indicatorMotionEnabled.value = animate && indicator.value.ready;

    indicator.value = { left, width, ready: true };
}

async function selectOption(index: number, focus = false)
{
    const option = props.options[index];

    if (!option || props.disabled || option[props.disabledField])
        return;

    model.value = option[props.valueField];
    await nextTick();

    if (focus)
        optionElements()[index]?.focus();
}

// Nośnik opcji dla narzędzi agenta: kliknięcie w niego jest kliknięciem przycisku tej opcji, więc
// wybór przechodzi drogą człowieka (blokada opcji, emit modelu, obsługa widoku). Domyślne przełączenie
// nośnika jest wstrzymane - jego zaznaczenie pokazuje wyłącznie wybór zapisany w modelu
function clickOption(index: number)
{
    optionElements()[index]?.click();
}

function moveSelection(index: number, offset: number)
{
    const available = enabledIndexes();

    if (available.length === 0)
        return;

    const current = Math.max(available.indexOf(index), 0);
    const next = (current + offset + available.length) % available.length;

    void selectOption(available[next], true);
}

function onKeydown(event: KeyboardEvent, index: number)
{
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp')
    {
        event.preventDefault();
        moveSelection(index, -1);
    }
    else if (event.key === 'ArrowRight' || event.key === 'ArrowDown')
    {
        event.preventDefault();
        moveSelection(index, 1);
    }
    else if (event.key === 'Home' || event.key === 'End')
    {
        event.preventDefault();

        const available = enabledIndexes();
        const target = event.key === 'Home' ? available.at(0) : available.at(-1);

        if (target !== undefined)
            void selectOption(target, true);
    }
}

function tabIndex(index: number)
{
    if (selectedIndex.value >= 0)
        return selectedIndex.value === index ? 0 : -1;

    return enabledIndexes().at(0) === index ? 0 : -1;
}

watch([model, () => props.options], ([value], [previous]) => measureIndicator(!Object.is(value, previous)), { deep: true, flush: 'post' });

onMounted(() =>
{
    resizeObserver = new ResizeObserver(() => measureIndicator(false));

    if (root.value)
        resizeObserver.observe(root.value);

    measureIndicator();
});

onBeforeUnmount(() => resizeObserver?.disconnect());
</script>

<template>
    <div
        ref="root"
        class="relative inline-flex h-[30px] shrink-0 gap-[2px] self-start rounded-[8px] border border-border bg-surface p-[2px] box-border dark:border-border-dark dark:bg-surface-dark"
        :class="[disabled ? formControlDisabledSurfaceClasses : [], full ? 'w-full' : '']"
        role="radiogroup"
        :aria-label="ariaLabel"
        :aria-disabled="disabled || undefined"
        data-form-segmented
    >
        <span
            class="pointer-events-none absolute inset-y-[2px] z-0 rounded-[6px] border border-[color-mix(in_oklch,var(--color-accent)_40%,transparent)] bg-accent-soft dark:bg-accent-soft-dark"
            :class="[
                indicator.ready ? 'opacity-100' : 'opacity-0',
                disabled ? '!border-field-disabled-border !bg-field-disabled-border dark:!border-field-disabled-border-dark dark:!bg-field-disabled-border-dark' : '',
                indicatorMotionEnabled ? '[transition:left_0.24s_cubic-bezier(.3,.8,.3,1.05),width_0.24s_cubic-bezier(.3,.8,.3,1.05),opacity_0.15s] motion-reduce:transition-none' : 'transition-none'
            ]"
            :data-segmented-motion="indicatorMotionEnabled ? 'animated' : 'static'"
            :style="indicatorStyle"
            aria-hidden="true"
            data-form-segmented-indicator
        />
        <IdeoTooltip
            v-for="(option, index) in options"
            :key="String(option[valueField])"
            :tooltip="option.tooltip"
            @keydown="onKeydown($event, index)"
            :tabindex="(disabled || option[disabledField]) && option.tooltip ? 0 : undefined"
            :aria-label="(disabled || option[disabledField]) && option.tooltip ? option[textField] : undefined"
             :class="full ? contentWeighted ? 'min-w-0 flex-[1_1_auto]' : 'min-w-0 flex-1' : 'shrink-0'"
            class="rounded-[6px] outline-none focus-visible:ring-[3px] focus-visible:ring-[color-mix(in_oklch,var(--color-accent)_20%,transparent)]"
        >
            <button
                type="button"
                role="radio"
                :aria-checked="option[valueField] === model"
                :aria-label="iconOnly ? option[textField] : undefined"
                :disabled="disabled || option[disabledField]"
                :tabindex="tabIndex(index)"
                :class="[
                    'relative z-1 inline-flex h-[24px] min-w-0 cursor-pointer items-center justify-center gap-[5px] whitespace-nowrap rounded-[6px] border-0 bg-transparent px-[10px] text-ui-helper/none font-[500] outline-none [transition:color_0.15s] focus-visible:ring-[3px] focus-visible:ring-[color-mix(in_oklch,var(--color-accent)_20%,transparent)] disabled:cursor-not-allowed disabled:opacity-100 disabled:!text-field-disabled-fg dark:disabled:!text-field-disabled-fg-dark',
                    full ? 'w-full px-[6px]!' : '',
                    iconOnly ? '!w-[32px] !gap-0 !px-0' : '',
                    (disabled || option[disabledField]) && option.tooltip ? 'pointer-events-none' : '',
                    option[valueField] === model ? 'text-accent-strong dark:text-accent-strong-dark' : 'text-fg-muted hover:text-fg dark:text-fg-muted-dark dark:hover:text-fg-dark'
                ]"
                data-form-segmented-option
                :data-segment-index="index"
                @click="selectOption(index)"
            >
                <IdeoIcon v-if="option[iconField]" :name="option[iconField]" :size="12" aria-hidden="true" />
                <span v-if="!iconOnly" class="min-w-0 truncate"><slot name="label" :option="option">{{ option[textField] }}</slot></span>
            </button>
        </IdeoTooltip>
        <template v-if="name">
            <input
                v-for="(option, index) in options"
                :key="'carrier-' + String(option[valueField])"
                type="radio"
                :name="name"
                :checked="option[valueField] === model"
                :disabled="disabled || option[disabledField]"
                :data-choice-value="JSON.stringify(option[valueField])"
                hidden
                tabindex="-1"
                aria-hidden="true"
                @click.prevent="clickOption(index)"
            >
        </template>
    </div>
</template>
